/**
 * Boolean op. A NEW structure from an EXPLICIT ORDERED list of
 * steps over named structures, with backward inter-step references. Each step
 * folds its ordered operands LEFT-TO-RIGHT under its single operation; `#k`
 * reuses the k-th step's result (1-based, strictly before this step); the last
 * step's result is the composite. There is NO formula and NO operator
 * precedence (ui_specs "show steps, no hidden precedence"). The op→engine-token
 * mapping (`union→'or'`, `intersect→'and'`, `subtract→'not'`) is the slice's
 * safety choke-point, golden-tested per op. Every operand is resolved via the
 * shared resolver/guards; the composite is computed ONCE then committed
 * unchanged (preview == apply).
 */
import {
    toListed,
    resolveOneStructure,
    resolveSourceForNewStructure,
    reasonOf,
    commitComputed,
    type Brand,
    type ComputedGeometry,
    type OpDeps,
    type ValidateResult,
    type CommitResult,
} from '../core';
import type {
    BooleanOperationInput,
    BooleanOperationStepInput,
} from '../../contract';

export type BooleanInput = BooleanOperationInput;
export type BooleanStepInput = BooleanOperationStepInput;

/**
 * The engine boolean-op token. This is the single, golden-tested mapping
 * choke-point (`union→'or'`, `intersect→'and'`, `subtract→'not'`); these
 * values are identical to the engine `BooleanOperation` string enum, so the
 * production adapter forwards the token unchanged — this stays engine-free.
 */
export type EngineBooleanOp = 'or' | 'and' | 'not' | 'xor';

/** One binary engine input: a resolved source ROI, or a prior step result. */
export type BooleanEngineOperand =
    | { readonly kind: 'roi'; readonly roiNumber: number }
    | { readonly kind: 'step'; readonly handle: unknown };

export type BooleanDeps = OpDeps<ComputedGeometry> & {
    readonly geometry: {
        /** Binary combine; returns a NEW opaque composite (no ROI mutated). */
        applyBoolean(
            op: EngineBooleanOp,
            left: BooleanEngineOperand,
            right: BooleanEngineOperand
        ): Promise<ComputedGeometry>;
        /**
         * Free an intermediate result no longer needed (GPU texture
         * lifecycle — every binary combine allocates a new texture; only the
         * composite survives to commit). This never releases the composite, so
         * preview==apply is unaffected.
         */
        releaseComputed?(handle: unknown): void;
    };
};

/* ── Stage 1: resolve (operands + op mapping + step-ref + invariants) ── */

/** A resolved operand: a concrete source ROI, or a prior step's result. */
type ResolvedOperand =
    | { readonly kind: 'roi'; readonly roiNumber: number }
    | { readonly kind: 'step'; readonly stepIndex: number };
interface ResolvedStep {
    readonly op: EngineBooleanOp;
    readonly operands: ReadonlyArray<ResolvedOperand>;
}
interface BooleanValidatedData {
    readonly steps: ReadonlyArray<ResolvedStep>;
    /** Volume of the first resolved ROI operand (step 1, operand 1) — the
     *  schema-uniform `volumeDeltaCm3` anchor. */
    readonly firstOperandVolumeCm3: number;
    readonly newRoi: string;
}
/** Only `resolveBoolean` mints this — preview cannot be skipped. */
export type BooleanValidated = Brand<BooleanValidatedData, 'BooleanValidated'>;

/**
 * The locked agent-op → engine-token map AND the single source of the valid
 * op set (the proposal-gate parser derives its op whitelist from this so it
 * cannot drift). The mapping matches the toolbar boolean's operation labels:
 * Unite→OR, Intersection→AND, Subtract→AND_NOT, Exclude→XOR.
 */
export const OP_TO_ENGINE: Readonly<
    Record<BooleanStepInput['op'], EngineBooleanOp>
> = {
    union: 'or',
    intersect: 'and',
    subtract: 'not',
    xor: 'xor',
};

/** A `#k` step reference (1-based). Anything else is an ROI name. The `#k`
 *  syntax must stay in sync with the contract schema operand description
 *  (`booleanStepSchema`). */
const STEP_REF = /^#(\d+)$/;

/** True when an operand is a `#k` back-reference rather than an ROI name. */
export function isStepReference(operand: string): boolean {
    return STEP_REF.test(operand);
}

export function resolveBoolean(
    input: BooleanInput,
    deps: BooleanDeps
): ValidateResult<BooleanValidated> {
    const { steps, newRoi } = input;
    // Structural invariants first (pure, no GPU): ≥1 step, each step a known
    // op with ≥2 operands, no formula/precedence (one op per step).
    if (steps.length === 0) {
        return { kind: 'invalid', reason: 'at least one step is required' };
    }
    for (const step of steps) {
        if (!(step.op in OP_TO_ENGINE)) {
            return {
                kind: 'invalid',
                reason: `unknown boolean operation '${step.op}'`,
            };
        }
        if (step.operands.length < 2) {
            return {
                kind: 'invalid',
                reason: 'each step needs at least two operands',
            };
        }
    }

    const listed = deps.structures.map(toListed);

    // Step 1's first operand is always an ROI name (`#k` is backward-only and
    // step 1 has no prior step), so it co-validates the new-structure name
    // via the shared `resolveSourceForNewStructure` seam.
    const firstName = steps[0].operands[0];
    if (isStepReference(firstName)) {
        return {
            kind: 'invalid',
            reason:
                'step 1 cannot reference a prior step ' +
                '(references must be backward)',
        };
    }
    const firstResolved = resolveSourceForNewStructure(
        firstName,
        newRoi,
        deps.structures,
        'boolean operation',
        'combine'
    );
    if (firstResolved.kind !== 'source') {
        return firstResolved;
    }
    const firstOperandVolumeCm3 = firstResolved.source.volumeCm3;

    const resolvedSteps: ResolvedStep[] = [];
    for (let s = 0; s < steps.length; s += 1) {
        const step = steps[s];
        const operands: ResolvedOperand[] = [];
        for (let o = 0; o < step.operands.length; o += 1) {
            const token = step.operands[o];
            const refMatch = STEP_REF.exec(token);
            if (refMatch) {
                const k = Number(refMatch[1]); // 1-based prior step
                // Backward-only, in range, not self/forward, not #0.
                if (k < 1 || k >= s + 1) {
                    return {
                        kind: 'invalid',
                        reason:
                            `step ${s + 1} references '${token}' which is not ` +
                            'a prior step (references must be backward)',
                    };
                }
                operands.push({ kind: 'step', stepIndex: k - 1 });
                continue;
            }
            // An ROI-name operand: reuse the same shared resolve+guard as
            // every structure-creating op (single source — cannot drift).
            const resolved =
                s === 0 && o === 0
                    ? firstResolved // already resolved+guarded above
                    : resolveOneStructure(
                          token,
                          deps.structures,
                          listed,
                          'boolean operand',
                          'combine'
                      );
            if (resolved.kind !== 'source') {
                return resolved;
            }
            operands.push({
                kind: 'roi',
                roiNumber: resolved.source.roiNumber,
            });
        }
        resolvedSteps.push({ op: OP_TO_ENGINE[step.op], operands });
    }

    const data: BooleanValidatedData = {
        steps: resolvedSteps,
        firstOperandVolumeCm3,
        newRoi,
    };
    const validated = data as BooleanValidated;
    return { kind: 'validated', validated };
}

/* ── Stage 2: preview (compute the composite ONCE) ── */

interface BooleanPreviewedData {
    readonly computed: ComputedGeometry;
    readonly newRoi: string;
    readonly volumeDeltaCm3: number;
}
/** Only `previewBoolean` mints this; `commitBoolean` accepts only this, so
 *  the applied composite IS the previewed composite (no recompute path). */
export type BooleanPreviewed = Brand<BooleanPreviewedData, 'BooleanPreviewed'>;

export type BooleanPreviewResult =
    | { readonly kind: 'previewed'; readonly previewed: BooleanPreviewed }
    | { readonly kind: 'failed'; readonly reason: string };

export async function previewBoolean(
    validated: BooleanValidated,
    deps: BooleanDeps
): Promise<BooleanPreviewResult> {
    // Evaluate the explicit ordered steps deterministically: each step folds
    // its operands LEFT-TO-RIGHT under its single op; a `#k` operand binds
    // the (already-computed, earlier) k-th step's FINAL result. The composite
    // is the LAST step's result — computed exactly ONCE here, then committed
    // unchanged (preview == apply, no recompute path).
    const stepResults: ComputedGeometry[] = [];
    // Each binary combine allocates a GPU texture. A within-step partial is
    // dead once the next combine consumes it, so free it immediately — peak use
    // then scales with step count, not operand count. Step-final results (a
    // later `#k` may read them) and the surviving composite are freed at the
    // end. `produced` holds only the still-live, previewBoolean-owned textures,
    // so the end/error cleanup frees exactly those (no leak, no double-free).
    const produced: ComputedGeometry[] = [];
    const release = (keep?: ComputedGeometry): void => {
        const free = deps.geometry.releaseComputed;
        if (!free) {
            return;
        }
        for (const p of produced) {
            if (p !== keep) {
                free(p.handle);
            }
        }
    };
    // Free one now-dead intermediate and drop it from the live set so the
    // end/error cleanup never frees it a second time.
    const freeOne = (g: ComputedGeometry): void => {
        const idx = produced.indexOf(g);
        if (idx !== -1) {
            produced.splice(idx, 1);
        }
        deps.geometry.releaseComputed?.(g.handle);
    };
    const toEngine = (op: ResolvedOperand): BooleanEngineOperand =>
        op.kind === 'roi'
            ? { kind: 'roi', roiNumber: op.roiNumber }
            : { kind: 'step', handle: stepResults[op.stepIndex].handle };

    let composite: ComputedGeometry | undefined;
    try {
        for (const step of validated.steps) {
            let acc: BooleanEngineOperand = toEngine(step.operands[0]);
            // The previous within-step partial; freed once the next combine
            // consumes it. operands[0] is a borrowed ROI or a kept `#k` result,
            // so it is never this — only partials this loop produced are freed.
            let prevPartial: ComputedGeometry | undefined;
            let stepResult: ComputedGeometry | undefined;
            for (let i = 1; i < step.operands.length; i += 1) {
                stepResult = await deps.geometry.applyBoolean(
                    step.op,
                    acc,
                    toEngine(step.operands[i])
                );
                produced.push(stepResult);
                if (prevPartial) {
                    freeOne(prevPartial);
                }
                prevPartial = stepResult;
                acc = { kind: 'step', handle: stepResult.handle };
            }
            // Each step has ≥2 operands (validated), so stepResult is set; it is
            // the step result — kept until the end (a later `#k` may read it).
            stepResults.push(stepResult as ComputedGeometry);
            composite = stepResult;
        }
    } catch (err) {
        // Result-not-throw across the boundary; the model is told. Free
        // every texture produced before the failure (no leak on error).
        release();
        return { kind: 'failed', reason: reasonOf(err) };
    }
    // A composite that encloses no volume (e.g. a disjoint intersect) must be
    // surfaced, never silently written.
    if (composite === undefined || !(composite.resultVolumeCm3 > 0)) {
        release();
        return {
            kind: 'failed',
            reason: 'the resulting structure would be empty',
        };
    }
    // Free every intermediate; the composite survives untouched to commit
    // (preview == apply — the composite handle is never released).
    release(composite);
    const previewed = {
        computed: composite,
        newRoi: validated.newRoi,
        volumeDeltaCm3:
            composite.resultVolumeCm3 - validated.firstOperandVolumeCm3,
    } as BooleanPreviewed;
    return { kind: 'previewed', previewed };
}

/* ── Stage 3: commit (write the ALREADY-computed composite to a NEW ROI) ── */

export async function commitBoolean(
    previewed: BooleanPreviewed,
    deps: BooleanDeps
): Promise<CommitResult> {
    return commitComputed(previewed, deps.commit.createNewStructure);
}
